import { Link } from "react-router-dom";
import {
  Landmark,
  TrendingUp,
  ShieldCheck,
  Compass,
  TreePine,
  Wheat,
  Building2,
  Shield,
  Columns,
  Users,
  Scale,
  Lock,
  Briefcase,
  Stethoscope,
  UserRound,
  Rocket,
  Home as HomeIcon,
  UsersRound,
  Star,
  Globe2,
  Store,
  Calculator,
  Camera,
} from "lucide-react";
import {
  CONTINUUM_STRIP,
  FIRM_SERVICE_CARDS,
  WE_SERVE,
  DARRYL_PHOTO,
  JOURNAL_ENTRIES,
  PRESS_CITATIONS,
} from "@/lib/content";
import SEO from "@/components/SEO";
import { DashTitle } from "@/components/DashTitle";
import HeroStarIntro from "@/components/HeroStarIntro";
import HomeHero from "@/components/HomeHero";
import FeaturedPullquote from "@/components/FeaturedPullquote";
import ArchiveStrip from "@/components/ArchiveStrip";
import MidPageCTA from "@/components/MidPageCTA";
import TestimonialsStrip from "@/components/TestimonialsStrip";
import RegulatoryAlert from "@/components/RegulatoryAlert";
import PressStrip from "@/components/PressStrip";
import { firmOrganizationSchema, webSiteSchema, faqPageSchema, founderPersonSchema } from "@/lib/schemas";

// Curated high-intent Q&A for the Home page FAQ schema.
// Emitted only as structured data (not visible on the page) — powers
// FAQ rich results in Google + citation snippets in AI Overviews / ChatGPT.
const HOME_FAQ = [
  {
    q: "What is Lone Star Counsel?",
    a: "Pratt Law Group, PLLC d/b/a Lone Star Counsel is a boutique Texas business, wealth, and legacy law firm founded and managed by Darryl V. Pratt — a Texas attorney and CPA with nearly 30 years of experience. Headquartered in Frisco, Texas, the firm advises business owners, physicians, professionals, entrepreneurs, real estate investors, franchisees, franchisors, and high-net-worth families throughout the state.",
  },
  {
    q: "Where are Lone Star Counsel's offices located?",
    a: "Lone Star Counsel maintains five Texas offices: Frisco (principal office), Round Rock (Austin metro), Boerne (San Antonio Hill Country), The Woodlands (Houston North), and Sugar Land (West Houston). All secondary offices are by appointment only.",
  },
  {
    q: "What practice areas does Lone Star Counsel handle?",
    a: "The firm's core practice areas include Business Counsel (entity formation, contracts, mergers & acquisitions, succession planning, franchises, medical practices, nonprofit organizations), Trusts & Estate Planning, Probate & Estate Administration, Guardianship Services, Bankruptcy & Financial Restructuring (Chapter 7, Chapter 11, Subchapter V), and Asset Protection Strategies.",
  },
  {
    q: "Does Lone Star Counsel handle Chapter 11 or Subchapter V bankruptcy?",
    a: "Yes. The firm regularly represents Texas businesses in Chapter 11 reorganizations and Subchapter V small business bankruptcies. Individual clients are also represented in Chapter 7 and personal reorganization matters. Darryl V. Pratt has nearly 30 years of restructuring experience representing debtors, creditors, and trustees.",
  },
  {
    q: "Who is the founder of Lone Star Counsel?",
    a: "The firm is founded and managed by Darryl V. Pratt, a Texas attorney and Certified Public Accountant (CPA). He personally handles every engagement and coordinates with Of Counsel affiliates when a matter requires specialized depth. He has practiced law in Texas for nearly 30 years.",
  },
  {
    q: "Does Lone Star Counsel serve Indian and South Asian families and business owners with cross-border matters?",
    a: "Yes. The firm advises Indian and South Asian families and business owners on cross-border tax, estate planning, EB-5 investor immigration, U.S. business formation, and pre-immigration tax planning. We assist U.S. citizens, permanent residents, Indian nationals, non-U.S. citizens, and multi-generational families with holdings in both India and the United States.",
  },
  {
    q: "How do I schedule a consultation with Lone Star Counsel?",
    a: "You may request a private conference via the firm's intake form at LoneStarCounsel.com/intake or by calling the Frisco principal office. Every new client engagement begins with a confidential consultation. The first hour of counsel is on the firm.",
  },
  {
    q: "What is the firm's tagline or slogan?",
    a: "Lone Star Counsel's positioning is 'Attorney. CPA. Business Advisor.' — reflecting founder Darryl V. Pratt's dual license as a Texas attorney and Certified Public Accountant. The firm's registered motto is 'Planning Today. Protecting Tomorrow.®' and it advises clients across the full continuum: Build. Grow. Protect. Restructure. Transition. Preserve.",
  },
];

const ICONS = {
  Landmark, TrendingUp, ShieldCheck, Compass, TreePine, Wheat,
  Building2, ShieldStar: Shield, Columns, Users, Scale, Lock,
  Briefcase, Stethoscope, UserRound, Rocket, Home: HomeIcon, UsersRound, Globe2, Store,
  Calculator, Camera,
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <HeroStarIntro />
      <SEO
        title="Texas Business, Wealth & Legacy Counsel"
        description="Lone Star Counsel — Texas Business, Wealth & Legacy Counsel. Advising entrepreneurs, business owners, professionals, high-net-worth individuals, and families through every stage of the business and wealth lifecycle."
        path="/"
        schema={[
          firmOrganizationSchema(),
          webSiteSchema(),
          founderPersonSchema(),
          faqPageSchema(HOME_FAQ),
        ]}
      />

      {/* ================================================================
          HERO — traffic-aware. HomeHero resolves the correct variant on
          the client (via window.__lscHero, pre-computed before hydration)
          and always renders the protected texas_flagship at SSR /
          prerender time. Homepage copy, layout, CTAs, and metadata are
          preserved regardless of selected variant.
      ================================================================= */}
      <HomeHero>
        <div className="max-w-2xl" data-testid="hero-copy">
          <h1
            className="font-display text-ivory leading-[1.02] text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem]"
            style={{
              letterSpacing: "0.005em",
              fontVariant: "small-caps",
              textShadow: "0 2px 24px rgba(0,0,0,0.55)",
            }}
            data-testid="hero-headline"
          >
            Counsel for Business.<br />
            Protection for Generations.
          </h1>

          <div className="mt-8 flex items-center gap-4" data-testid="hero-ornament">
            <span className="h-px w-16 bg-brass/80" />
            <Star size={11} className="text-brass fill-brass" />
            <span className="h-px w-16 bg-brass/80" />
          </div>

          <p
            className="mt-8 max-w-md text-lg text-ivory/90 font-light leading-relaxed"
            style={{ textShadow: "0 1px 12px rgba(0,0,0,0.55)" }}
            data-testid="hero-subhead"
          >
            Strategic legal counsel for business owners, families, and professionals.
          </p>

          <div className="mt-10 flex flex-wrap gap-4" data-testid="hero-ctas">
              <Link to="/intake" className="btn-gold" data-testid="hero-cta-intake">
                Schedule a Consultation
              </Link>
              <Link
                to="/practice"
                className="btn-ghost !border-ivory/70 !text-ivory hover:!bg-ivory hover:!text-navy"
                data-testid="hero-cta-practice"
              >
                Our Services
              </Link>
            </div>
        </div>
      </HomeHero>

      {/* ================================================================
          REGULATORY ALERT — thin banner surfacing timely federal or
          state regulatory changes affecting clients of the firm.
      ================================================================= */}
      <RegulatoryAlert />

      {/* ================================================================
          JOURNEY BAR — light strip. Six chapters, icons + subtitles.
      ================================================================= */}
      <section className="bg-linen py-12 md:py-14" data-testid="journey-bar">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-8">
            {CONTINUUM_STRIP.map((c) => {
              const Icon = ICONS[c.icon];
              return (
                <div
                  key={c.title}
                  className="flex items-center gap-4 justify-center lg:justify-start"
                  data-testid={`journey-${c.title.toLowerCase()}`}
                >
                  <div className="shrink-0 h-11 w-11 rounded-full flex items-center justify-center text-brass">
                    {Icon && <Icon size={22} strokeWidth={1.25} />}
                  </div>
                  <div className="leading-tight">
                    <div
                      className="font-display text-lg md:text-xl text-navy"
                      style={{ letterSpacing: "0.02em" }}
                    >
                      {c.title}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-slate2/70 mt-1 font-medium">
                      {c.sub}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================================================================
          BRAND PROMISE — the firm's trademarked taglines. Elegant strip.
      ================================================================= */}
      <section
        className="bg-ivory pt-20 md:pt-24 pb-4 md:pb-6 text-center"
        data-testid="brand-promise-section"
      >
        <div className="max-w-3xl mx-auto px-6">
          {/* Firm identity block — name · descriptor · discipline strip */}
          <div className="mb-10" data-testid="firm-identity-block">
            <div className="font-display text-navy text-2xl md:text-3xl tracking-tight" data-testid="firm-identity-name">
              Lone Star Counsel
            </div>
            <div className="mt-2 font-cormorant italic text-slate2 text-lg md:text-xl" data-testid="firm-identity-role">
              Attorneys &amp; Advisors
            </div>
            <div className="mt-4 text-[11px] md:text-xs uppercase tracking-[0.32em] text-brass font-medium" data-testid="firm-identity-disciplines">
              Business
              <span className="text-brass/40 mx-2">•</span>
              Estate Planning
              <span className="text-brass/40 mx-2">•</span>
              Probate
              <span className="text-brass/40 mx-2">•</span>
              Trusts
              <span className="text-brass/40 mx-2">•</span>
              Succession
            </div>
          </div>

          <div className="inline-flex items-center gap-3 mb-6">
            <span className="h-px w-8 bg-brass/60" />
            <Star size={10} className="text-brass fill-brass" />
            <span className="h-px w-8 bg-brass/60" />
          </div>
          <p
            className="font-display text-navy text-3xl md:text-4xl lg:text-[2.5rem] leading-[1.15]"
            style={{ letterSpacing: "0.005em" }}
            data-testid="brand-tagline-primary"
          >
            <span>Attorney.</span>{" "}
            <span>CPA.</span>{" "}
            <span className="whitespace-nowrap">Business Advisor.</span>
          </p>

        </div>
      </section>

      {/* ================================================================
          PRACTICE — ivory. Six cards in one row with subtle borders,
          exactly like the mockup. No Roman numerals, no editorial polish.
      ================================================================= */}
      <section className="bg-ivory py-16 md:py-20" data-testid="full-service-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="text-center">
            <div className="inline-flex items-center gap-4 text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
              <span className="h-px w-10 bg-brass/60" />
              A Boutique Services Law Firm
              <span className="h-px w-10 bg-brass/60" />
            </div>
            <div className="mt-2 flex justify-center">
              <Star size={10} className="text-brass fill-brass" />
            </div>
            <h2 className="mt-6 font-display text-navy leading-[1.1] text-3xl md:text-4xl lg:text-[2.75rem]">
              From Today&apos;s Decisions to Tomorrow&apos;s Legacy
            </h2>
            <p className="mt-5 max-w-3xl mx-auto text-base md:text-lg text-slate2 font-light leading-relaxed">
              We deliver sophisticated legal and strategic advice across the full lifecycle of your
              business and personal wealth—so you can build, grow, protect, and pass it on with
              confidence.
            </p>
          </div>

          <div
            className="mt-14 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
            data-testid="full-service-grid"
          >
            {FIRM_SERVICE_CARDS.map((card) => {
              const Icon = ICONS[card.icon];
              return (
                <Link
                  to={card.href}
                  key={card.title}
                  className="group bg-ivory border border-transparent hover:border-brass hover:shadow-[0_16px_40px_-16px_rgba(8,13,27,0.20)] transition-all p-5 md:p-6 text-center flex flex-col items-center"
                  data-testid={`service-card-${card.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <div className="inline-flex items-center justify-center h-12 w-12 text-brass group-hover:text-oxblood transition-colors">
                    {Icon && <Icon size={34} strokeWidth={1.15} />}
                  </div>
                  <h3
                    className="mt-4 font-display text-[15px] md:text-base text-navy leading-tight uppercase font-medium"
                    style={{ letterSpacing: "0.06em" }}
                  >
                    {card.title}
                  </h3>
                  <ul className="mt-4 space-y-1.5 text-sm text-slate2 font-light">
                    {card.items.map((it) => (
                      <li key={it}>{it}</li>
                    ))}
                  </ul>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================================================================
          DALLAS SKYLINE mid-page banner — a full-bleed banner grounding the
          firm in Dallas and North Texas. Overlay tagline in ivory small caps.
      ================================================================= */}
      <section
        className="relative bg-mahogany overflow-hidden"
        data-testid="dallas-banner-section"
      >
        <div className="relative w-full h-[380px] md:h-[440px] lg:h-[500px]">
          <img
            src="/img/office-dfw.jpg"
            alt="Dallas–Fort Worth Metroplex skyline at golden hour"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          {/* Elegant navy scrim for text legibility */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(30,58,95,0.72) 0%, rgba(30,58,95,0.40) 45%, rgba(30,58,95,0.15) 100%)",
            }}
          />
          {/* CONTENT */}
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-[1600px] mx-auto w-full px-6 md:px-12 lg:px-16">
              <div className="max-w-xl" data-testid="dallas-banner-copy">
                <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
                  <span className="h-px w-10 bg-brass/70" />
                  Frisco &nbsp;·&nbsp; Dallas &nbsp;·&nbsp; North Texas
                </div>
                <h2
                  className="mt-6 font-display text-ivory leading-[1.05] text-3xl md:text-4xl lg:text-5xl"
                  style={{ letterSpacing: "0.005em", fontVariant: "small-caps" }}
                >
                  Rooted in North Texas.<br />
                  Serving the Lone Star State.
                </h2>
                <p className="mt-6 max-w-lg text-ivory/85 font-light leading-relaxed text-base md:text-lg">
                  Based in Texas, providing experienced counsel across Dallas–Fort Worth, Austin,
                  San Antonio, Houston, Corpus Christi, Brownsville/Harlingen, El Paso, Midland/Odessa,
                  Lubbock, Amarillo, Big Spring, Abilene, Tyler, Sherman/Denison, and the entire Metroplex.
                </p>
                <div className="mt-8">
                  <Link
                    to="/locations"
                    className="btn-gold"
                    data-testid="dallas-banner-cta"
                  >
                    See Our Offices
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          TRUSTED COUNSEL + WE SERVE — navy panel with brass-rule divider.
      ================================================================= */}
      <section className="bg-ivory py-24 md:py-28" data-testid="trusted-serve-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-4">
              <Star size={11} className="text-brass fill-brass" />
              <span className="h-px w-14 bg-brass/60" />
            </div>
            <h2 className="mt-6 font-display text-navy leading-[1.1] text-4xl md:text-5xl">
              Trusted Counsel.<br />
              Proven Guidance.<br />
              Enduring Relationships.
            </h2>
            <p className="mt-8 max-w-md text-slate2 font-light leading-relaxed text-lg">
              Our clients rely on us for practical solutions, thoughtful strategies, and a level of
              service that goes far beyond the ordinary. We measure success by your peace of mind
              today and the legacy you leave tomorrow.
            </p>
            <Link
              to="/about"
              className="mt-10 inline-flex items-center gap-2 btn-gold"
              data-testid="meet-attorneys-cta"
            >
              Meet Our Counsel
            </Link>
          </div>

          <div className="lg:col-span-6 lg:pl-8">
            <div
              className="text-center lg:text-left text-xs uppercase tracking-[0.36em] text-brass mb-10 font-medium"
              data-testid="we-serve-kicker"
            >
              We Serve
            </div>
            <div
              className="grid grid-cols-2 md:grid-cols-3 gap-y-10 gap-x-6"
              data-testid="we-serve-grid"
            >
              {WE_SERVE.map((s) => {
                const Icon = ICONS[s.icon];
                return (
                  <div
                    key={s.label}
                    className="text-center"
                    data-testid={`we-serve-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="inline-flex items-center justify-center h-12 w-12 text-brass">
                      {Icon && <Icon size={32} strokeWidth={1.15} />}
                    </div>
                    <div className="mt-3 text-[11px] uppercase tracking-[0.24em] text-navy/90 leading-snug font-medium">
                      {s.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          INDIAN & SOUTH ASIAN COUNSEL — dedicated editorial callout
          with cross-border expertise, Fort Bend positioning.
      ================================================================= */}
      <section className="panel-navy py-24 md:py-32" data-testid="south-asian-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="kicker kicker-ivory">Cross-Border Counsel</div>
              <h2 className="mt-6 font-display text-4xl md:text-6xl leading-[0.98] text-ivory">
                For Indian and<br/>
                <span className="italic font-cormorant text-brass">South Asian families.</span>
              </h2>
              <p className="mt-8 font-cormorant italic text-xl md:text-2xl text-ivory/85 leading-snug">
                Counsel for Indian and South Asian families, professionals, and business owners
                throughout Texas.
              </p>
              <div className="mt-10">
                <Link to="/locations/houston-west" className="btn-brass" data-testid="south-asian-cta">
                  Sugar Land · Fort Bend Office →
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-ivory/85 font-light text-base md:text-lg leading-relaxed">
              <p>
                Lone Star Counsel proudly serves Indian and South Asian individuals, families,
                entrepreneurs, physicians, professionals, and business owners throughout Texas.
              </p>
              <p>
                We understand that clients with family, property, business interests, inheritances,
                or financial accounts in both the United States and India may face unique legal,
                tax, estate-planning, and succession-planning considerations. Our firm provides
                thoughtful, culturally respectful counsel designed to coordinate your U.S. legal
                planning with your family relationships, business objectives, and long-term legacy
                goals.
              </p>
              <div className="pt-4 hairline-top">
                <p className="text-brass font-cormorant italic text-lg md:text-xl leading-snug">
                  We assist U.S. citizens, permanent residents, Indian nationals, non-U.S. citizens,
                  and families with cross-border connections.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          FEATURED IN THIS ISSUE — editorial pullquote strip surfacing the
          most recent Journal entry. Reads like a magazine spread; distinct
          from the 3-card journal grid below.
      ================================================================= */}
      <FeaturedPullquote />

      {/* ================================================================
          FROM THE ARCHIVE — three evergreen essays across three
          disciplines (Trusts, Bankruptcy, Business Succession). Shows
          first-time visitors the firm's breadth without cluttering.
      ================================================================= */}
      <ArchiveStrip />

      {/* ================================================================
          MID-PAGE CTA — restrained conversion band. One italic line +
          brass button. Catches readers who've engaged with the editorial
          blocks above and are ready to act.
      ================================================================= */}
      <MidPageCTA />

      {/* ================================================================
          TESTIMONIALS — anonymized editorial pullquotes. Attribution held
          in confidence per the Texas Rules of Professional Conduct.
      ================================================================= */}
      <TestimonialsStrip variant="linen" />

      {/* ================================================================
          PRESS — "As Featured In" strip. Renders nothing until real
          entries are added to PRESS_CITATIONS in /lib/content.js. Per
          Texas Rules of Professional Conduct 7.02, only verified press
          coverage may be published here — the empty array is intentional.
      ================================================================= */}
      {PRESS_CITATIONS.length > 0 && (
        <section className="bg-ivory py-16 md:py-24" data-testid="press-section">
          <PressStrip
            publications={Array.from(new Set(PRESS_CITATIONS.map((c) => c.publication)))}
            className="max-w-[1600px] mx-auto px-6 md:px-12"
          />
        </section>
      )}

      {/* ================================================================
          THE JOURNAL — 3 latest editorial pieces with the AI-generated
          covers. Ivory backdrop, restrained, magazine-style.
      ================================================================= */}
      <section className="bg-ivory py-24 md:py-32" data-testid="journal-preview-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="inline-flex items-center gap-4 text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
                <span className="h-px w-10 bg-brass/60" />
                The Journal
              </div>
              <h2 className="mt-4 font-display text-navy text-3xl md:text-4xl leading-[1.15] max-w-2xl">
                Weekly articles on topics of interest — Texas business law, family wealth, and the professional practice of legal craft.
              </h2>
              <p className="mt-4 font-cormorant italic text-lg md:text-xl text-slate2 max-w-xl">
                Plain-spoken. No filler.
              </p>
            </div>
            <Link
              to="/journal"
              className="text-[11px] uppercase tracking-[0.32em] text-brass hover:text-oxblood transition-colors font-medium border-b-0 hover:border-oxblood pb-1"
              data-testid="journal-preview-all"
            >
              All Essays →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {JOURNAL_ENTRIES.slice(0, 3).map((j, i) => (
              <Link
                to={`/journal/${j.slug}`}
                key={j.title}
                className="group block"
                data-testid={`journal-preview-${i}`}
              >
                <div className={`aspect-[4/3] overflow-hidden ${j.coverSquare ? "bg-[#031E3D]" : "bg-parchment"}`}>
                  <img
                    src={j.coverSmall || j.cover}
                    alt={j.coverAlt || j.title}
                    className={`w-full h-full ${j.coverSquare ? "object-contain" : "object-cover transition-transform duration-700 group-hover:scale-[1.03]"}`}
                    loading="lazy"
                  />
                </div>
                <div className="mt-6 flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-slate2/80">
                  <span>{j.category}</span>
                  <span>{j.date} · {j.minutes} min</span>
                </div>
                <h3 className="mt-4 font-display text-2xl md:text-[1.65rem] text-navy leading-[1.15] group-hover:text-oxblood transition-colors">
                  <DashTitle text={j.title} />
                </h3>
                <p className="mt-3 text-slate2 font-light leading-relaxed text-sm md:text-base">
                  {j.excerpt}
                </p>
                <div className="mt-6 flex items-center gap-3 pt-4 border-t border-parchment">
                  <img
                    src={DARRYL_PHOTO}
                    alt="Darryl V. Pratt"
                    className="w-9 h-9 rounded-full object-cover object-top grayscale-[10%]"
                    loading="lazy"
                  />
                  <div className="leading-tight">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-navy font-semibold">
                      {j.author}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-brass">
                      Managing Partner
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
